/**
 * Button, IconButton, ButtonGroup, LinkAction.
 *
 * Controls have visible rank, and the rank is the API: primary / default (secondary) /
 * ghost / danger, plus disabled and loading. A screen that needs a fifth rank needs a
 * different layout, not a fifth colour.
 *
 * Three things here are policy rather than styling:
 *
 *  1. `locked` — the affordance doctrine 1 and 2 depend on. A control that is disallowed
 *     BY POLICY renders visibly locked, with a padlock and a tooltip stating the rule, and
 *     it is genuinely inert. This is how "Transfer" appears on every call row: not hidden
 *     (the operator must see that we refuse to bridge), not enabled (we never do), and not
 *     merely greyed — grey reads as "broken", locked reads as "deliberate".
 *
 *  2. `gated` — a state-changing AI action that needs authorisation renders in the orchid
 *     AI hue with a lock. Read-only AI actions render calm and ordinary. This is not
 *     decoration: it is the difference an operator uses to decide whether to click
 *     something at 2 AM.
 *
 *  3. `confirm` — anything irreversible cannot be a bare onClick. Passing `confirm` opens
 *     a ConfirmDialog that states exactly what will happen before the handler runs.
 */
import { forwardRef, useState } from 'react';
import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';
import { ConfirmDialog } from './Modal.tsx';
import type { ConfirmSpec } from './Modal.tsx';

export type ButtonVariant = 'default' | 'primary' | 'danger' | 'ghost' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconAfter?: IconName;
  loading?: boolean;
  block?: boolean;
  /** Orchid + lock: this AI action changes state and is policy-gated (doctrine 2). */
  gated?: boolean;
  /**
   * Disallowed by policy. Pass the sentence that explains the rule — it becomes the
   * tooltip and the accessible description, and the control is inert.
   */
  locked?: string;
  /** Required for anything irreversible. States exactly what will happen. */
  confirm?: ConfirmSpec;
  /**
   * When `confirm.typeToConfirm` is set, the handler is called with the string the operator
   * typed. Anything that has to prove the confirmation to a server must use that argument.
   */
  onClick?: (confirmedText?: string) => void | Promise<void>;
  children?: ReactNode;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'default',
    size = 'md',
    icon,
    iconAfter,
    loading = false,
    block = false,
    gated = false,
    locked,
    confirm,
    onClick,
    children,
    className,
    disabled,
    type = 'button',
    title,
    ...rest
  },
  ref,
): ReactElement {
  const [confirming, setConfirming] = useState(false);
  const isLocked = typeof locked === 'string';
  const isDisabled = disabled === true || loading || isLocked;
  const iconSize = size === 'sm' ? 12 : 14;

  const run = (typed?: string): void => {
    void onClick?.(typed);
  };

  return (
    <>
      <button
        {...rest}
        ref={ref}
        type={type}
        className={['nd-btn', className].filter(Boolean).join(' ')}
        data-variant={variant === 'default' ? undefined : variant}
        data-size={size === 'md' ? undefined : size}
        data-block={block ? 'true' : undefined}
        data-gated={gated && !isLocked ? 'true' : undefined}
        data-locked={isLocked ? 'true' : undefined}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        title={
          title ??
          locked ??
          (gated ? 'Policy-gated action — it changes state and is recorded against your name' : undefined)
        }
        onClick={() => {
          if (isDisabled) return;
          if (confirm) {
            setConfirming(true);
            return;
          }
          run();
        }}
      >
        {loading ? <span className="nd-btn-spin" aria-hidden="true" /> : null}
        {!loading && (isLocked || gated) ? <Icon name="lock" size={iconSize} /> : null}
        {!loading && !isLocked && !gated && icon ? <Icon name={icon} size={iconSize} /> : null}
        {children}
        {iconAfter ? <Icon name={iconAfter} size={iconSize} /> : null}
      </button>

      {confirm ? (
        <ConfirmDialog
          open={confirming}
          spec={confirm}
          onCancel={() => setConfirming(false)}
          onConfirm={(typed) => {
            setConfirming(false);
            run(typed);
          }}
        />
      ) : null}
    </>
  );
});

export type IconButtonProps = Omit<ButtonProps, 'children' | 'icon' | 'iconAfter'> & {
  icon: IconName;
  /** Required — an icon-only control with no name is invisible to a screen reader. */
  label: string;
  /**
   * The quiet 30px chrome button from the top bar and the drawer header, rather than the
   * bordered 32px button. This is the right choice for toolbar affordances.
   */
  chrome?: boolean;
  /** Adds the border and surface, for the one chrome button that must read as pressable. */
  bordered?: boolean;
  /** A dot in the corner: something is waiting behind this control. */
  pip?: boolean;
};

export function IconButton({
  icon,
  label,
  size = 'md',
  chrome = false,
  bordered = false,
  pip = false,
  className,
  title,
  disabled,
  locked,
  onClick,
  ...rest
}: IconButtonProps): ReactElement {
  const isLocked = typeof locked === 'string';

  if (chrome) {
    // Deliberately does NOT spread `rest`: the chrome variant renders a bare DOM button,
    // and component-only props (variant, gated, confirm…) would leak onto the element.
    return (
      <button
        type="button"
        id={rest.id}
        aria-haspopup={rest['aria-haspopup']}
        aria-expanded={rest['aria-expanded']}
        className={['nd-iconbtn', className].filter(Boolean).join(' ')}
        data-bordered={bordered ? 'true' : undefined}
        data-size={size === 'sm' ? 'sm' : undefined}
        aria-label={label}
        title={title ?? locked ?? label}
        disabled={disabled === true || isLocked}
        onClick={() => {
          if (disabled === true || isLocked) return;
          void onClick?.();
        }}
      >
        <Icon name={isLocked ? 'lock' : icon} size={16} />
        {pip ? <span className="nd-iconbtn-pip" aria-hidden="true" /> : null}
      </button>
    );
  }

  return (
    <Button
      {...rest}
      size={size}
      disabled={disabled}
      locked={locked}
      onClick={onClick}
      className={['nd-btn-icon', className].filter(Boolean).join(' ')}
      aria-label={label}
      title={title ?? locked ?? label}
    >
      <Icon name={icon} size={size === 'sm' ? 12 : 14} />
    </Button>
  );
}

/** Buttons welded into one control: one border, hairlines between. */
export function ButtonGroup({ children, label }: { children: ReactNode; label?: string }): ReactElement {
  return (
    <div className="nd-btngroup" role="group" aria-label={label}>
      {children}
    </div>
  );
}

/**
 * The quiet inline action — "Last 14 nights ›", "View all 90 ›". A real button, not a
 * styled span, so it is reachable by keyboard and announces itself.
 */
export function LinkAction({
  children,
  onClick,
  href,
  accent = false,
  iconAfter = 'chevron-right',
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  href?: string;
  accent?: boolean;
  iconAfter?: IconName | null;
  title?: string;
}): ReactElement {
  const inner = (
    <>
      {children}
      {iconAfter ? <Icon name={iconAfter} size={13} /> : null}
    </>
  );

  if (href) {
    return (
      <a className="nd-lnk" data-accent={accent ? 'true' : undefined} href={href} title={title}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" className="nd-lnk" data-accent={accent ? 'true' : undefined} onClick={onClick} title={title}>
      {inner}
    </button>
  );
}

export default Button;
